import { CommonModule, DecimalPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { LanguageModule, SharedLanguageService } from '@language';
import { ShAutocompleteService } from '../core/autocomplete/services/autocomplete.service';
import { ShActionItemComponent } from '../elements/components/action-item/action-item.component';
import { ShIconModule } from '../elements/components/icon/icon.module';
import { ShFieldLabelComponent } from '../form-controls/components/_core/field-label/field-label.component';
import { ShFormGroupComponent } from '../form-controls/components/_core/form-group/form-group.component';
import { ShFormCheckboxListComponent } from '../form-controls/components/form-checkbox-list/main/form-checkbox-list.component';
import { ShFormCheckboxComponent } from '../form-controls/components/form-checkbox/main/form-checkbox.component';
import { ShFormDateComponent } from '../form-controls/components/form-date/main/form-date.component';
import { ShFormEDTFComponent } from '../form-controls/components/form-edtf/main/form-edtf.component';
import { ShFormListComponent } from '../form-controls/components/form-list/main/form-list.component';
import { ShFormMultiSelectComponent } from '../form-controls/components/form-multi-select/main/form-multi-select.component';
import { ShFormNumberInputComponent } from '../form-controls/components/form-number/main/form-number.component';
import { ShFormRadioListComponent } from '../form-controls/components/form-radio-list/main/form-radio-list.component';
import { ShFormSelectComponent } from '../form-controls/components/form-select/main/form-select.component';
import { ShFormTextInputComponent } from '../form-controls/components/form-text/main/form-text.component';
import { ShFormTextareaInputComponent } from '../form-controls/components/form-textarea/main/form-textarea.component';
import { ShFormTimeComponent } from '../form-controls/components/form-time/main/form-time.component';
import { ShFormTypedValueListComponent } from '../form-controls/components/form-typed-value-list/main/form-typed-value-list.component';
import { ShComplexInputFieldComponent } from './components/input/complex-input-fields/complex-input-field/complex-input-field.component';
import { ShComplexListInputFieldComponent } from './components/input/complex-input-fields/complex-list-input-field/complex-list-input-field.component';
import { ShComplexMapInputFieldComponent } from './components/input/complex-input-fields/complex-map-input-field/complex-map-input-field.component';
import { ShListInputFieldComponent } from './components/input/complex-input-fields/list-input-field/list-input-field.component';
import { ShMapInputFieldComponent } from './components/input/complex-input-fields/map-input-field/map-input-field.component';
import { ShMultiItemInputFieldComponent } from './components/input/complex-input-fields/multi-item-input-field/multi-item-input-field.component';
import { ShTimerInputFieldComponent } from './components/input/complex-input-fields/timer-input-field/timer-input-field.component';
import { ShGenericInputFieldComponent } from './components/input/generic-input-field/generic-input-field.component';
import { ShDoubleInputFieldComponent } from './components/input/simple-input-fields/double-input-field/double-input-field.component';
import { ShEDTFInputFieldComponent } from './components/input/simple-input-fields/edtf-input-field/edtf-input-field.component';
import { ShEnumInputFieldComponent } from './components/input/simple-input-fields/enum-input-field/enum-input-field.component';
import { ShFramesInputFieldComponent } from './components/input/simple-input-fields/frames-input-field/frames-input-field.component';
import { ShHiddenInputFieldComponent } from './components/input/simple-input-fields/hidden-input-field/hidden-input-field.component';
import { ShLargeTextInputFieldComponent } from './components/input/simple-input-fields/large-text-input-field/large-text-input-field.component';
import { ShLongInputFieldComponent } from './components/input/simple-input-fields/long-input-field/long-input-field.component';
import { ShSimpleInputFieldComponent } from './components/input/simple-input-fields/simple-input-field/simple-input-field.component';
import { ShTimeCodeInputFieldComponent } from './components/input/simple-input-fields/time-code-input-field/time-code-input-field.component';
import { ShUnsupportedInputFieldComponent } from './components/input/simple-input-fields/unsupported-input-field/unsupported-input-field.component';
import { ShUrlInputFieldComponent } from './components/input/simple-input-fields/url-input-field/url-input-field.component';
import { ShMergeStrategyControlComponent } from './components/merge-strategy-control/merge-strategy-control.component';
import { ShBaseGenericReadonlyFieldComponent } from './components/readonly/base-generic-readonly-field/base-generic-readonly-field.component';
import { ShComplexListReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-list-readonly-field/complex-list-readonly-field.component';
import { ShComplexMapReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-map-readonly-field/complex-map-readonly-field.component';
import { ShComplexReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-readonly-field/complex-readonly-field.component';
import { ShLabeledUrlReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/labeled-url-readonly-field/labeled-url-readonly-field.component';
import { ShListReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/list-readonly-field/list-readonly-field.component';
import { ShMapReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/map-readonly-field/map-readonly-field.component';
import { ShMultiItemReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/multi-item-readonly-field/multi-item-readonly-field.component';
import { ShTimerReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/timer-readonly-field/timer-readonly-field.component';
import { ShGenericReadonlyFieldComponent } from './components/readonly/generic-readonly-field/generic-readonly-field.component';
import { ShBooleanReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/boolean-readonly-field/boolean-readonly-field.component';
import { ShDateReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/date-readonly-field/date-readonly-field.component';
import { ShDoubleReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/double-readonly-field/double-readonly-field.component';
import { ShEDTFReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/edtf-readonly-field/edtf-readonly-field.component';
import { ShEnumReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/enum-readonly-field/enum-readonly-field.component';
import { ShFramesReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/frames-readonly-field/frames-readonly-field.component';
import { ShHiddenReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/hidden-readonly-field/hidden-readonly-field.component';
import { ShLargeTextReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/large-text-readonly-field/large-text-readonly-field.component';
import { ShLongReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/long-readonly-field/long-readonly-field.component';
import { ShSimpleReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/simple-readonly-field/simple-readonly-field.component';
import { ShTimeCodeReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/time-code-readonly-field/time-code-readonly-field.component';
import { ShUnsupportedReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/unsupported-readonly-field/unsupported-readonly-field.component';
import { ShUrlReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/url-readonly-field/url-readonly-field.component';
import { ShProfileInputFieldsetComponent } from './containers/profile-input-fieldset/profile-input-fieldset.component';
import { ShProfileReadonlyFieldsetComponent } from './containers/profile-readonly-fieldset/profile-readonly-fieldset.component';
import { ShInjectionPointDirective } from './directives/injection-point.directive';

@NgModule({
    declarations: [
        ShBooleanReadonlyFieldComponent,
        ShComplexInputFieldComponent,
        ShComplexReadonlyFieldComponent,
        ShComplexListInputFieldComponent,
        ShComplexListReadonlyFieldComponent,
        ShComplexMapInputFieldComponent,
        ShComplexMapReadonlyFieldComponent,
        ShDateReadonlyFieldComponent,
        ShEDTFInputFieldComponent,
        ShEDTFReadonlyFieldComponent,
        ShEnumInputFieldComponent,
        ShEnumReadonlyFieldComponent,
        ShFramesInputFieldComponent,
        ShFramesReadonlyFieldComponent,
        ShGenericInputFieldComponent,
        ShGenericReadonlyFieldComponent,
        ShInjectionPointDirective,
        ShLargeTextInputFieldComponent,
        ShLargeTextReadonlyFieldComponent,
        ShListInputFieldComponent,
        ShListReadonlyFieldComponent,
        ShLongInputFieldComponent,
        ShLongReadonlyFieldComponent,
        ShDoubleInputFieldComponent,
        ShDoubleReadonlyFieldComponent,
        ShMergeStrategyControlComponent,
        ShMultiItemInputFieldComponent,
        ShMultiItemReadonlyFieldComponent,
        ShMapInputFieldComponent,
        ShMapReadonlyFieldComponent,
        ShProfileInputFieldsetComponent,
        ShProfileReadonlyFieldsetComponent,
        ShSimpleInputFieldComponent,
        ShSimpleReadonlyFieldComponent,
        ShTimeCodeInputFieldComponent,
        ShTimeCodeReadonlyFieldComponent,
        ShTimerInputFieldComponent,
        ShTimerReadonlyFieldComponent,
        ShUnsupportedInputFieldComponent,
        ShUnsupportedReadonlyFieldComponent,
        ShHiddenInputFieldComponent,
        ShHiddenReadonlyFieldComponent,
        ShUrlInputFieldComponent,
        ShUrlReadonlyFieldComponent,
        ShLabeledUrlReadonlyFieldComponent
    ],
    providers: [ShAutocompleteService, SharedLanguageService, DecimalPipe],
    exports: [
        ShBaseGenericReadonlyFieldComponent,
        ShBooleanReadonlyFieldComponent,
        ShComplexInputFieldComponent,
        ShComplexReadonlyFieldComponent,
        ShComplexListInputFieldComponent,
        ShComplexListReadonlyFieldComponent,
        ShComplexMapInputFieldComponent,
        ShComplexMapReadonlyFieldComponent,
        ShDateReadonlyFieldComponent,
        ShEDTFInputFieldComponent,
        ShEDTFReadonlyFieldComponent,
        ShEnumInputFieldComponent,
        ShEnumReadonlyFieldComponent,
        ShFramesInputFieldComponent,
        ShFramesReadonlyFieldComponent,
        ShGenericInputFieldComponent,
        ShGenericReadonlyFieldComponent,
        ShLargeTextInputFieldComponent,
        ShLargeTextReadonlyFieldComponent,
        ShListInputFieldComponent,
        ShListReadonlyFieldComponent,
        ShLongInputFieldComponent,
        ShLongReadonlyFieldComponent,
        ShDoubleInputFieldComponent,
        ShDoubleReadonlyFieldComponent,
        ShMultiItemInputFieldComponent,
        ShMultiItemReadonlyFieldComponent,
        ShMapInputFieldComponent,
        ShMapReadonlyFieldComponent,
        ShProfileInputFieldsetComponent,
        ShProfileReadonlyFieldsetComponent,
        ShSimpleInputFieldComponent,
        ShSimpleReadonlyFieldComponent,
        ShTimeCodeInputFieldComponent,
        ShTimeCodeReadonlyFieldComponent,
        ShTimerInputFieldComponent,
        ShTimerReadonlyFieldComponent,
        ShUnsupportedInputFieldComponent,
        ShUnsupportedReadonlyFieldComponent,
        ShHiddenInputFieldComponent,
        ShHiddenReadonlyFieldComponent,
        ShUrlInputFieldComponent,
        ShUrlReadonlyFieldComponent,
        ShLabeledUrlReadonlyFieldComponent
    ],
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        ShIconModule,
        LanguageModule.forRoot(),
        ShActionItemComponent,
        ShFormSelectComponent,
        ShFormTimeComponent,
        ShFormTextInputComponent,
        ShFormNumberInputComponent,
        ShFormTextareaInputComponent,
        ShFormRadioListComponent,
        ShFormMultiSelectComponent,
        ShFormEDTFComponent,
        ShFormDateComponent,
        ShFormCheckboxComponent,
        ShFormTypedValueListComponent,
        ShFormListComponent,
        ShFormCheckboxListComponent,
        ShFormGroupComponent,
        ShFieldLabelComponent,
        ShBaseGenericReadonlyFieldComponent
    ]
})
export class ShProfileFormModule {}

export { ShBaseGenericInputFieldComponent } from './components/input/base-generic-input-field/base-generic-input-field.component';
export { ShBaseGenericReadonlyFieldComponent } from './components/readonly/base-generic-readonly-field/base-generic-readonly-field.component';

export { ShBooleanInputFieldComponent } from './components/input/simple-input-fields/boolean-input-field/boolean-input-field.component';
export { ShBooleanReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/boolean-readonly-field/boolean-readonly-field.component';

export { ShComplexInputFieldComponent } from './components/input/complex-input-fields/complex-input-field/complex-input-field.component';
export { ShComplexReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-readonly-field/complex-readonly-field.component';

export { ShComplexListInputFieldComponent } from './components/input/complex-input-fields/complex-list-input-field/complex-list-input-field.component';
export { ShComplexListReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-list-readonly-field/complex-list-readonly-field.component';

export { ShComplexMapInputFieldComponent } from './components/input/complex-input-fields/complex-map-input-field/complex-map-input-field.component';
export { ShComplexMapReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/complex-map-readonly-field/complex-map-readonly-field.component';

export { ShDateInputFieldComponent } from './components/input/simple-input-fields/date-input-field/date-input-field.component';
export { ShDateReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/date-readonly-field/date-readonly-field.component';

export { ShEDTFInputFieldComponent } from './components/input/simple-input-fields/edtf-input-field/edtf-input-field.component';
export { ShEDTFReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/edtf-readonly-field/edtf-readonly-field.component';

export { ShEnumInputFieldComponent } from './components/input/simple-input-fields/enum-input-field/enum-input-field.component';
export { ShEnumReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/enum-readonly-field/enum-readonly-field.component';

export { ShFramesInputFieldComponent } from './components/input/simple-input-fields/frames-input-field/frames-input-field.component';
export { ShFramesReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/frames-readonly-field/frames-readonly-field.component';

export {
    GenericInputFieldDescriptor,
    ShGenericInputFieldComponent
} from './components/input/generic-input-field/generic-input-field.component';
export { ShGenericReadonlyFieldComponent } from './components/readonly/generic-readonly-field/generic-readonly-field.component';

export { ShInjectionPointDirective } from './directives/injection-point.directive';

export { ShLargeTextInputFieldComponent } from './components/input/simple-input-fields/large-text-input-field/large-text-input-field.component';
export { ShLargeTextReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/large-text-readonly-field/large-text-readonly-field.component';

export { ShLongInputFieldComponent } from './components/input/simple-input-fields/long-input-field/long-input-field.component';
export { ShLongReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/long-readonly-field/long-readonly-field.component';

export { ShDoubleInputFieldComponent } from './components/input/simple-input-fields/double-input-field/double-input-field.component';
export { ShDoubleReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/double-readonly-field/double-readonly-field.component';

export { ShListInputFieldComponent } from './components/input/complex-input-fields/list-input-field/list-input-field.component';
export { ShListReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/list-readonly-field/list-readonly-field.component';

export { ShMultiItemInputFieldComponent } from './components/input/complex-input-fields/multi-item-input-field/multi-item-input-field.component';
export { ShMultiItemReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/multi-item-readonly-field/multi-item-readonly-field.component';

export { ShMapInputFieldComponent } from './components/input/complex-input-fields/map-input-field/map-input-field.component';
export { ShMapReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/map-readonly-field/map-readonly-field.component';

export { ShProfileInputFieldsetComponent } from './containers/profile-input-fieldset/profile-input-fieldset.component';
export { ShProfileReadonlyFieldsetComponent } from './containers/profile-readonly-fieldset/profile-readonly-fieldset.component';

export { ShSimpleInputFieldComponent } from './components/input/simple-input-fields/simple-input-field/simple-input-field.component';
export { ShSimpleReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/simple-readonly-field/simple-readonly-field.component';

export { ShTimeCodeInputFieldComponent } from './components/input/simple-input-fields/time-code-input-field/time-code-input-field.component';
export { ShTimeCodeReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/time-code-readonly-field/time-code-readonly-field.component';

export { ShTimerInputFieldComponent } from './components/input/complex-input-fields/timer-input-field/timer-input-field.component';
export { ShTimerReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/timer-readonly-field/timer-readonly-field.component';

export { ShUnsupportedInputFieldComponent } from './components/input/simple-input-fields/unsupported-input-field/unsupported-input-field.component';
export { ShUnsupportedReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/unsupported-readonly-field/unsupported-readonly-field.component';

export { ShUrlInputFieldComponent } from './components/input/simple-input-fields/url-input-field/url-input-field.component';
export { ShUrlReadonlyFieldComponent } from './components/readonly/simple-readonly-fields/url-readonly-field/url-readonly-field.component';

export { ShLabeledUrlReadonlyFieldComponent } from './components/readonly/complex-readonly-fields/labeled-url-readonly-field/labeled-url-readonly-field.component';
